import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "react-query";

import { Loading } from "components";
import { getServiceList } from "apis";
import { ServiceCategory } from "types";
import { ServiceDTO } from "types/DTOs/service/service.dto";
import LoginPromptModal from "components/common/LoginPromptModal";
import { getImageUrl } from "utils/image.util";

const categories: { emoji: string; label: string; value: ServiceCategory | undefined }[] = [
  { emoji: "✨", label: "전체", value: undefined },
  { emoji: "💬", label: "수다·티타임", value: ServiceCategory.CHAT },
  { emoji: "🎮", label: "게임·듀오", value: ServiceCategory.GAME },
  { emoji: "🔮", label: "타로·운세", value: ServiceCategory.TAROT },
  { emoji: "💡", label: "고민·피드백", value: ServiceCategory.FEEDBACK },
  { emoji: "💻", label: "랜선 스터디", value: ServiceCategory.STUDY },
  { emoji: "✨", label: "자유주제", value: ServiceCategory.FREE },
];

type SortOption = "latest" | "price_asc" | "price_desc" | "review";
const sortOptions: { label: string; value: SortOption }[] = [
  { label: "최신순", value: "latest" },
  { label: "가격 낮은순", value: "price_asc" },
  { label: "가격 높은순", value: "price_desc" },
  { label: "예약 많은순", value: "review" },
];

const priceRanges = [
  { label: "전체", min: 0, max: Infinity },
  { label: "무료", min: 0, max: 0 },
  { label: "~1만원", min: 1, max: 10000 },
  { label: "1~3만원", min: 10000, max: 30000 },
  { label: "3~5만원", min: 30000, max: 50000 },
  { label: "5만원~", min: 50000, max: Infinity },
];

const getCategoryColor = (category: ServiceCategory): string => {
  switch (category) {
    case ServiceCategory.CHAT: return "bg-pink-50 text-pink-600";
    case ServiceCategory.GAME: return "bg-purple-50 text-purple-600";
    case ServiceCategory.TAROT: return "bg-violet-50 text-violet-600";
    case ServiceCategory.FEEDBACK: return "bg-amber-50 text-amber-600";
    case ServiceCategory.STUDY: return "bg-blue-50 text-blue-600";
    case ServiceCategory.FREE: return "bg-emerald-50 text-emerald-600";
    default: return "bg-achromatic-100 text-achromatic-500";
  }
};

export default function Explore() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<ServiceCategory | undefined>(undefined);
  const [sortBy, setSortBy] = useState<SortOption>("latest");
  const [priceRange, setPriceRange] = useState(0); // index into priceRanges
  const [showLoginModal, setShowLoginModal] = useState(false);

  const { data: servicesData, isLoading } = useQuery(
    ["exploreServices", selectedCategory],
    () => getServiceList(selectedCategory)
  );

  const services: ServiceDTO[] = servicesData?.data?.data ?? [];

  const isSlotAvailable = (s: any): boolean => {
    if (s.isBooked || !s.date) return false;
    const timeStr = s.endTime || s.startTime || "23:59:59";
    const slotDate = new Date(`${s.date}T${timeStr.length === 5 ? timeStr + ":00" : timeStr}`);
    return !isNaN(slotDate.getTime()) && slotDate.getTime() > Date.now();
  };

  const processedServices = useMemo(() => {
    let result = services.filter((s) => {
      const availableCount = s.schedules?.filter(isSlotAvailable).length ?? 0;
      return availableCount > 0;
    });

    // Text search
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (s) =>
          s.title.toLowerCase().includes(term) ||
          s.description.toLowerCase().includes(term) ||
          s.host?.name?.toLowerCase().includes(term)
      );
    }

    // Price filter
    const range = priceRanges[priceRange];
    if (range.max === 0) {
      result = result.filter((s) => s.price === 0);
    } else if (range.min > 0 || range.max < Infinity) {
      result = result.filter((s) => s.price >= range.min && s.price <= range.max);
    }

    // Sort
    switch (sortBy) {
      case "latest":
        result.sort((a, b) => new Date(b.createdAt ?? 0).getTime() - new Date(a.createdAt ?? 0).getTime());
        break;
      case "price_asc":
        result.sort((a, b) => a.price - b.price);
        break;
      case "price_desc":
        result.sort((a, b) => b.price - a.price);
        break;
      case "review":
        result.sort((a, b) => (b.schedules?.filter(isSlotAvailable).length ?? 0) - (a.schedules?.filter(isSlotAvailable).length ?? 0));
        break;
    }

    return result;
  }, [services, searchTerm, sortBy, priceRange]);

  const handleServiceClick = (serviceId: string) => {
    navigate(`/service/${serviceId}`);
  };

  return (
    <div className="animate-fadeIn">
      {/* Search + Filters */}
      <div className="bg-white border-b border-achromatic-50">
        <div className="mx-auto py-5">
          <div className="relative mb-4">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-achromatic-300 text-sm">🔍</span>
            <input
              type="text"
              placeholder="서비스명, 설명, 호스트로 검색..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 bg-achromatic-50 rounded-xl text-sm text-achromatic-800 placeholder:text-achromatic-300 focus:outline-none focus:ring-2 focus:ring-primary-200 transition-all"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-achromatic-300 hover:text-achromatic-500 text-lg"
              >
                ×
              </button>
            )}
          </div>

          {/* Category pills */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-1">
            {categories.map((cat) => (
              <button
                key={cat.label}
                onClick={() => setSelectedCategory(cat.value)}
                className={`flex items-center gap-1.5 whitespace-nowrap px-4 py-2 rounded-xl text-[13px] font-bold transition-all ${
                  selectedCategory === cat.value
                    ? "bg-primary-500 text-white"
                    : "bg-achromatic-50 text-achromatic-500 hover:text-achromatic-700 hover:bg-achromatic-100"
                }`}
              >
                <span>{cat.emoji}</span>
                {cat.label}
              </button>
            ))}
          </div>

          {/* Sort + Price Filter Row */}
          <div className="flex items-center gap-3 mt-3 flex-wrap">
            {/* Sort select */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="px-3 py-1.5 text-[12px] bg-white border border-achromatic-100 rounded-lg text-achromatic-600 font-medium focus:outline-none focus:ring-1 focus:ring-primary-200 cursor-pointer"
            >
              {sortOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>

            {/* Price range pills */}
            <div className="flex gap-1.5 overflow-x-auto scrollbar-hide">
              {priceRanges.map((range, idx) => (
                <button
                  key={range.label}
                  onClick={() => setPriceRange(idx)}
                  className={`whitespace-nowrap px-3 py-1.5 rounded-lg text-[11px] font-semibold transition-all ${
                    priceRange === idx
                      ? "bg-achromatic-900 text-white"
                      : "bg-achromatic-50 text-achromatic-400 hover:bg-achromatic-100"
                  }`}
                >
                  {range.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Result count + Service grid */}
      <div className="mx-auto py-6">
        {!isLoading && (
          <div className="flex items-center justify-between mb-4">
            <p className="text-[13px] text-achromatic-500">
              <span className="font-bold text-achromatic-800">{processedServices.length}</span>개의 서비스
              {searchTerm && <span className="text-achromatic-400"> · "{searchTerm}" 검색결과</span>}
            </p>
          </div>
        )}

        {isLoading && <Loading />}

        {!isLoading && processedServices.length === 0 && (
          <div className="bg-white rounded-2xl p-12 text-center border border-achromatic-50">
            <span className="text-4xl block mb-3">📭</span>
            <p className="text-sm font-semibold text-achromatic-800 mb-1">
              {searchTerm ? "검색 결과가 없습니다" : "등록된 서비스가 없습니다"}
            </p>
            <p className="text-xs text-achromatic-400">
              {searchTerm
                ? "다른 검색어나 필터를 시도해보세요"
                : "호스트가 서비스를 등록하면 여기에 표시됩니다"}
            </p>
            {(searchTerm || priceRange !== 0) && (
              <button
                onClick={() => { setSearchTerm(""); setPriceRange(0); }}
                className="mt-4 px-4 py-2 text-[12px] font-bold text-primary-500 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
              >
                필터 초기화
              </button>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-4 mobile:grid-cols-1">
          {processedServices.map((service) => {
            const availableSlots = service.schedules?.filter(isSlotAvailable).length ?? 0;
            const mainImage = service.images && service.images.length > 0 ? service.images[0] : null;
            const imgUrl = mainImage ? getImageUrl(mainImage) : null;
            return (
              <div
                key={service.id}
                className="bg-white rounded-2xl overflow-hidden cursor-pointer border border-achromatic-50 hover:border-achromatic-100 hover:shadow-sm transition-all duration-200 group"
                onClick={() => handleServiceClick(service.id)}
              >
                {/* Thumbnail */}
                <div className="w-full bg-achromatic-50" style={{ aspectRatio: '3/2' }}>
                  {imgUrl ? (
                    <img src={imgUrl} alt={service.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-4xl bg-gradient-to-br from-achromatic-50 to-achromatic-100">
                      {service.category === ServiceCategory.CHAT ? '💬' : service.category === ServiceCategory.GAME ? '🎮' : service.category === ServiceCategory.TAROT ? '🔮' : service.category === ServiceCategory.FEEDBACK ? '💡' : service.category === ServiceCategory.STUDY ? '💻' : '✨'}
                    </div>
                  )}
                </div>

                <div className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-[11px] font-bold px-2.5 py-1 rounded-md ${getCategoryColor(service.category)}`}>
                      {service.category}
                    </span>
                    <span className={`text-[11px] font-semibold ${
                      availableSlots > 0 ? "text-primary-500" : "text-achromatic-300"
                    }`}>
                      {availableSlots > 0 ? `${availableSlots}개 가능` : "마감"}
                    </span>
                  </div>
                  <h3 className="text-[15px] font-bold text-achromatic-900 mb-1 line-clamp-1 group-hover:text-primary-500 transition-colors">{service.title}</h3>
                  <p className="text-xs text-achromatic-400 line-clamp-2 mb-3">{service.description}</p>
                  <div className="flex justify-between items-center pt-3 border-t border-achromatic-50">
                    <div className="flex items-center gap-2">
                      {service.host?.profileImage ? (
                        <img
                          src={getImageUrl(service.host.profileImage)}
                          alt="host"
                          className="w-6 h-6 rounded-full object-cover"
                        />
                      ) : (
                        <div className="w-6 h-6 bg-primary-50 rounded-full flex items-center justify-center text-[10px] font-bold text-primary-500">
                          {service.host?.name?.charAt(0) ?? "?"}
                        </div>
                      )}
                      <span className="text-xs text-achromatic-500">{service.host?.name ?? "호스트"}</span>
                      <span className="text-[10px] text-achromatic-300">·</span>
                      <span className="text-[11px] text-achromatic-300">{service.durationMinutes}분</span>
                    </div>
                    <div>
                      <span className="text-[15px] font-extrabold text-achromatic-900">{service.price.toLocaleString()}</span>
                      <span className="text-[11px] text-achromatic-400 ml-0.5">원</span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        message="서비스를 예약하려면 로그인이 필요합니다"
      />
    </div>
  );
}
